import { getErrorMessage } from '@xver/utils/error';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { UpdateContainerRequest } from '@xver/apis/v1/kufer/containers/model';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { CONTAINER_DETAIL_QUERY_KEY } from './useContainerQuery';
import { BUILD_LIST_QUERY_KEY } from './useBuildListQuery';

const CONTAINER_LIST_QUERY_KEY = ['apis.v1.kufer.containers.list'];

const REDEPLOY_NOTIFICATION_ID = 'container-redeploy';

interface UseContainerMutationsOptions {
    onDeleteSuccess?: () => void;
}

export function useContainerMutations(
    containerId: string | undefined,
    options?: UseContainerMutationsOptions
) {
    const { t } = useTranslation('containerEngineInstance');
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const invalidateDetail = async () => {
        if (!containerId) return;
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: [CONTAINER_DETAIL_QUERY_KEY, containerId],
            }),
            queryClient.invalidateQueries({ queryKey: CONTAINER_LIST_QUERY_KEY }),
        ]);
    };

    const showError = (err: unknown) => {
        notifications.show({
            title: t('mutations.errorTitle', 'Action failed'),
            message: getErrorMessage(err),
            icon: <IconExclamationCircle size={18} color="#eb5757" />,
        });
    };

    const showSuccess = (title: string, message: string) => {
        notifications.show({
            title,
            message,
            icon: <IconCheck size={18} color="#22c55e" />,
        });
    };

    const restartMutation = useMutation({
        mutationFn: () => {
            if (!containerId) throw new Error('containerId is required');
            return Endpoints.v1.Kufer.Containers.restartContainer(containerId);
        },
        onSuccess: async () => {
            showSuccess(
                t('mutations.restartTitle', 'Container restart triggered'),
                t('mutations.restartMessage', 'A new revision is being deployed.')
            );
            await invalidateDetail();
        },
        onError: showError,
    });

    const redeployMutation = useMutation({
        mutationFn: () => {
            if (!containerId) throw new Error('containerId is required');
            return Endpoints.v1.Kufer.Containers.redeployContainer(containerId);
        },
        onSuccess: async () => {
            showSuccess(
                t('mutations.redeployTitle', 'Container redeploy triggered'),
                t('mutations.redeployMessage', 'A new revision is being deployed.')
            );
            await invalidateDetail();
        },
        onError: showError,
    });

    const rebuildMutation = useMutation({
        mutationFn: () => {
            if (!containerId) throw new Error('containerId is required');
            return Endpoints.v1.Kufer.Builds.rebuildContainer(containerId);
        },
        onSuccess: async () => {
            showSuccess(
                t('mutations.rebuildTitle', 'Container rebuild triggered'),
                t('mutations.rebuildMessage', 'A new build has started.')
            );
            if (containerId) {
                await queryClient.invalidateQueries({
                    queryKey: [BUILD_LIST_QUERY_KEY, containerId],
                });
            }
            await invalidateDetail();
        },
        onError: showError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!containerId) throw new Error('containerId is required');
            return Endpoints.v1.Kufer.Containers.deleteContainer(containerId);
        },
        onSuccess: async () => {
            showSuccess(
                t('mutations.deleteTitle', 'Container deleted'),
                t('mutations.deleteMessage', 'All resources will be cleaned up.')
            );
            await queryClient.invalidateQueries({ queryKey: CONTAINER_LIST_QUERY_KEY });
            if (options?.onDeleteSuccess) {
                options.onDeleteSuccess();
            } else {
                navigate('/container-engine');
            }
        },
        onError: showError,
    });

    const updateMutation = useMutation({
        mutationFn: (data: UpdateContainerRequest) => {
            if (!containerId) throw new Error('containerId is required');
            return Endpoints.v1.Kufer.Containers.updateContainer(containerId, data);
        },
        onMutate: () => {
            notifications.show({
                id: REDEPLOY_NOTIFICATION_ID,
                loading: true,
                autoClose: false,
                withCloseButton: false,
                title: t('mutations.redeployingTitle', 'Redeploying'),
                message: t(
                    'mutations.redeployingMessage',
                    'Applying your changes and rolling out a new revision…'
                ),
            });
        },
        onSuccess: async (data) => {
            if (data?.status === 'failed') {
                notifications.update({
                    id: REDEPLOY_NOTIFICATION_ID,
                    loading: false,
                    color: 'red',
                    icon: <IconExclamationCircle size={18} color="#eb5757" />,
                    title: t('mutations.deployFailedTitle', 'Deploy failed'),
                    message:
                        data.message ||
                        t(
                            'mutations.deployFailedMessage',
                            "The new revision didn't become ready — check the logs."
                        ),
                    autoClose: 6000,
                    withCloseButton: true,
                });
            } else {
                notifications.update({
                    id: REDEPLOY_NOTIFICATION_ID,
                    loading: false,
                    color: 'green',
                    icon: <IconCheck size={18} color="#22c55e" />,
                    title: t('mutations.deployedTitle', 'Container deployed'),
                    message: t('mutations.deployedMessage', 'Your changes are live.'),
                    autoClose: 4000,
                    withCloseButton: true,
                });
            }
            await invalidateDetail();
        },
        onError: (err) => {
            notifications.update({
                id: REDEPLOY_NOTIFICATION_ID,
                loading: false,
                color: 'red',
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
                title: t('mutations.errorTitle', 'Action failed'),
                message: getErrorMessage(err),
                autoClose: 6000,
                withCloseButton: true,
            });
        },
    });

    return {
        restartMutation,
        redeployMutation,
        rebuildMutation,
        deleteMutation,
        updateMutation,
    };
}
